import { useMemo, type JSX, type ReactNode } from 'react'
import {
  PendoProvider,
  CdnPendoProvider,
  ConsolePendoProvider,
  NoopPendoProvider,
  type IPendoProvider,
  type PendoVisitor,
  type PendoAccount,
} from '@revalize/pendo/frontend'
import { useApi } from '@/hooks/useAppApi'
import { ConfigService } from '@/services/ConfigService'
import { logger } from '@/utils/logger'
import { isDevEnvironmentName } from '@shared/features'

export type PendoProviderKind = 'console' | 'noop' | 'cdn'

interface PendoUser { id?: string; email?: string; name?: string; tenantId?: string }

/** Pure: choose the provider implementation for the current environment. */
export function selectPendoProvider(opts: { isDevAuthMode: boolean; apiKey: string | null }): {
  kind: PendoProviderKind
  provider: IPendoProvider
} {
  if (opts.isDevAuthMode) return { kind: 'console', provider: new ConsolePendoProvider() }
  if (!opts.apiKey) return { kind: 'noop', provider: new NoopPendoProvider() }
  return { kind: 'cdn', provider: new CdnPendoProvider() }
}

/** The tenant id the backend falls back to when a user's tenant cannot be resolved
 *  (backend/src/features/auth/routes.ts, getUserProfile — :124, :136, :150 as of 2026-09-03). */
const UNRESOLVED_TENANT_ID = 'default'

/** Staging names this repo actually uses (IaC/ECS convention). Combined with
 *  `isDevEnvironmentName`'s dev/development/local, this covers both of the
 *  repo's two `ENVIRONMENT` naming conventions — see `isNonProductionEnvironmentName`.
 *
 *  Kept LOCAL to Pendo rather than folded into `shared/src/features.ts`'s
 *  `DEV_ENVIRONMENTS`: that list has a narrower, security-relevant meaning
 *  ("is this a dev deployment, safe to mount DevTools on") consumed by
 *  `feature-gate.ts` and MCP auth — adding staging there would silently make
 *  dev-only features/tooling available in staging. Pendo's "non-production"
 *  concept is broader on purpose, so it gets its own list rather than
 *  widening one that other, unrelated call sites depend on staying narrow. */
const NON_PROD_STAGING_NAMES = ['stg', 'staging']

/** Whether an `ENVIRONMENT` value names a non-production deployment, across BOTH
 *  conventions this repo uses: IaC/ECS (`dev`/`stg`/`prd`) and the documented
 *  production env file / docker-compose default (`ENVIRONMENT=production`,
 *  `.env.prod.example:6`, `docker-compose.production.yml:130`,
 *  `backend/src/routes/config.ts:84` passing it straight through). */
function isNonProductionEnvironmentName(environment: string): boolean {
  return isDevEnvironmentName(environment) || NON_PROD_STAGING_NAMES.includes(environment)
}

/** Non-production ids are environment-qualified so that a Pendo subscription shared
 *  across dev/stg/prd does not mix internal traffic into production numbers.
 *
 *  FAILS CLOSED: an id is qualified ONLY when `environment` is a RECOGNISED
 *  non-production name (see `isNonProductionEnvironmentName`). Everything else —
 *  `'prd'`, `'production'`, an unrecognised value, a typo, a future environment
 *  name nobody has added here yet — is left UNQUALIFIED, i.e. treated as
 *  production. That asymmetry is deliberate: an id that should have been
 *  qualified but wasn't just mixes into a shared pool (a symptom, and fixable
 *  going forward); qualifying an id that should NOT have been touched rewrites a
 *  live production visitor's identity, which Pendo cannot undo (see below). When
 *  the environment name is unrecognised, do nothing.
 *
 *  Production ids are deliberately NOT qualified. Pendo is live, and changing a
 *  production visitor id does not rename a visitor — it creates a new one, orphaning
 *  that person's history and guide state, resetting first-visit, and inflating MAU
 *  (the metered unit) while both ids count in the rolling 30-day window. Support can
 *  merge ids many-to-one but cannot split one id across per-environment ids. */
function qualifyId(id: string, environment: string): string {
  return isNonProductionEnvironmentName(environment) ? `${environment}:${id}` : id
}

/** Pure: map the app user to a Pendo visitor + account (empty when unauthenticated).
 *
 * An unresolved tenant MUST NOT become an account. Pendo stamps `account.id` at write
 * time, so sending the shared literal 'default' places engineers from different
 * customers into one account, and merged accounts cannot be split afterwards. Sending
 * the visitor with no account keeps the events visitor-scoped instead. */
export function buildPendoIdentity(
  user: PendoUser | undefined,
  environment: string,
): { visitor?: PendoVisitor; account?: PendoAccount } {
  if (!user?.id) return {}
  const visitor: PendoVisitor = {
    id: qualifyId(user.id, environment),
    ...(user.email !== undefined && { email: user.email }),
    ...(user.name !== undefined && { full_name: user.name }),
  }
  if (!user.tenantId || user.tenantId === UNRESOLVED_TENANT_ID) return { visitor }
  return { visitor, account: { id: qualifyId(user.tenantId, environment) } }
}

/** Bridges @revalize/pendo's Pino-style ctx-first logger to the app's message-first
 *  one. Module scope on purpose: PendoService is memoized on [provider, logger], so a
 *  new adapter object each render would remount the initializer. */
export const pendoLoggerAdapter = {
  debug: (obj: object, msg?: string): void => { logger.debug(msg ?? 'pendo', { ...obj }) },
  info: (obj: object, msg?: string): void => { logger.info(msg ?? 'pendo', { ...obj }) },
  warn: (obj: object, msg?: string): void => { logger.warn(msg ?? 'pendo', { ...obj }) },
  error: (obj: object, msg?: string): void => {
    const { err, ...rest } = obj as { err?: unknown }
    // A non-Error `err` must not vanish. CdnPendoProvider wraps a third-party SDK,
    // and a DOMException is not `instanceof Error` in most browsers — dropping it
    // would make this adapter silently lose exactly the failures it exists to surface.
    if (err instanceof Error) { logger.error(msg ?? 'pendo', err, rest); return }
    logger.error(msg ?? 'pendo', undefined, err === undefined ? rest : { ...rest, err })
  },
}

export function PendoRoot({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useApi()
  const apiKey = ConfigService.getPendoApiKey()
  const isDevAuthMode = ConfigService.isDevAuthMode()

  const provider = useMemo<IPendoProvider>(
    () => selectPendoProvider({ isDevAuthMode, apiKey }).provider,
    [isDevAuthMode, apiKey],
  )

  const { visitor, account } = buildPendoIdentity(user ?? undefined, ConfigService.getEnvironment())

  return (
    <PendoProvider
      provider={provider}
      logger={pendoLoggerAdapter}
      {...(apiKey !== null && { apiKey })}
      {...(visitor !== undefined && { visitor })}
      {...(account !== undefined && { account })}
      enabled={!!user?.id}
      excludeAllText
    >
      {children}
    </PendoProvider>
  )
}
